/**
 * InvoicesPanel — VP-256. Zakładka „Faktury": pełna historia płatności ze Stripe
 * (numer, kwota, okres rozliczeniowy, status) z linkiem do PDF-a.
 *
 * Dane lecą na żywo ze Stripe przez `/api/billing/invoices` — konto rozliczane poza
 * Stripe (np. EasyCart) dostaje pustą listę i komunikat, a nie błąd.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Download, ExternalLink, FileText, Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type InvoiceStatus = "draft" | "open" | "paid" | "uncollectible" | "void" | "unknown";

type Invoice = {
  id: string;
  number: string | null;
  status: InvoiceStatus;
  amountPaid: number;
  amountDue: number;
  currency: string;
  issuedAt: string;
  periodStart: string | null;
  periodEnd: string | null;
  hostedUrl: string | null;
  pdfUrl: string | null;
};

type InvoicesResponse = {
  invoices: Invoice[];
  hasMore: boolean;
  nextCursor: string | null;
  source: "stripe" | "none";
};

const STATUS_CLASSES: Record<InvoiceStatus, string> = {
  paid: "bg-green-600/15 text-green-400 border-green-500/25",
  open: "bg-orange-600/15 text-orange-400 border-orange-500/25",
  draft: "bg-white/[0.06] text-white/50 border-white/[0.12]",
  uncollectible: "bg-red-600/15 text-red-400 border-red-500/25",
  void: "bg-white/[0.06] text-white/40 border-white/[0.12]",
  unknown: "bg-white/[0.06] text-white/50 border-white/[0.12]",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatPeriod(from: string | null, to: string | null): string {
  if (!from && !to) return "—";
  return `${formatDate(from)} – ${formatDate(to)}`;
}

function formatAmount(amount: number, currency: string): string {
  return `${amount.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

export default function InvoicesPanel() {
  const { t } = useTranslation("billingPanel");
  // Kursory kolejnych stron — trzymamy stos, żeby dało się wrócić bez zgadywania.
  const [cursors, setCursors] = useState<string[]>([]);
  const currentCursor = cursors[cursors.length - 1] ?? null;

  const { data, isLoading, isError, isFetching } = useQuery<InvoicesResponse>({
    queryKey: ["/api/billing/invoices", currentCursor],
    queryFn: () => {
      const params = new URLSearchParams();
      if (currentCursor) params.set("startingAfter", currentCursor);
      const query = params.toString();
      return apiRequest("GET", `/api/billing/invoices${query ? `?${query}` : ""}`);
    },
    placeholderData: keepPreviousData,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-white/40">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-8 text-center">
        <p className="text-white/50 text-sm">{t("invoices.error")}</p>
      </div>
    );
  }

  const invoices = data?.invoices ?? [];

  if (invoices.length === 0) {
    return (
      <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-10 text-center">
        <FileText className="w-6 h-6 text-white/20 mx-auto mb-3" />
        <p className="text-white/50 text-sm">
          {data?.source === "none" ? t("invoices.noStripeAccount") : t("invoices.empty")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-white text-sm font-medium">{t("invoices.title")}</h3>
        <p className="text-white/40 text-xs mt-1">{t("invoices.subtitle")}</p>
      </div>

      <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("invoices.columnNumber")}</TableHead>
              <TableHead>{t("invoices.columnPeriod")}</TableHead>
              <TableHead>{t("invoices.columnIssued")}</TableHead>
              <TableHead className="text-right">{t("invoices.columnAmount")}</TableHead>
              <TableHead>{t("invoices.columnStatus")}</TableHead>
              <TableHead className="text-right">{t("invoices.columnActions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell className="font-medium text-white">
                  {invoice.number ?? t("invoices.noNumber")}
                </TableCell>
                <TableCell className="text-white/60">
                  {formatPeriod(invoice.periodStart, invoice.periodEnd)}
                </TableCell>
                <TableCell className="text-white/60">{formatDate(invoice.issuedAt)}</TableCell>
                <TableCell className="text-right text-white">
                  {formatAmount(
                    invoice.status === "paid" ? invoice.amountPaid : invoice.amountDue,
                    invoice.currency
                  )}
                </TableCell>
                <TableCell>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${STATUS_CLASSES[invoice.status]}`}
                  >
                    {t(`invoices.status.${invoice.status}`)}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {invoice.pdfUrl && (
                      <a
                        href={invoice.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t("invoices.downloadPdf")}
                        aria-label={t("invoices.downloadPdf")}
                        className="text-white/40 hover:text-white transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {invoice.hostedUrl && (
                      <a
                        href={invoice.hostedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t("invoices.openInStripe")}
                        aria-label={t("invoices.openInStripe")}
                        className="text-white/40 hover:text-white transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {(cursors.length > 0 || data?.hasMore) && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCursors((prev) => prev.slice(0, -1))}
            disabled={cursors.length === 0 || isFetching}
            className="text-xs text-white/50 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            {t("invoices.previousPage")}
          </button>
          <button
            onClick={() =>
              setCursors((prev) => (data?.nextCursor ? [...prev, data.nextCursor] : prev))
            }
            disabled={!data?.hasMore || isFetching}
            className="text-xs text-white/50 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            {t("invoices.nextPage")}
          </button>
        </div>
      )}
    </div>
  );
}
